import { MyreApp } from '@myre/common-core';
import { defaultLangLocale, LangLocale, languages } from '@myre/common-i18n';
import { Scope } from '@myre/domain-i18n-core';
import { DynamicEnvService } from '@myre/ui-domain-http';
import {
  APP_BASE_HREF_PROVIDER,
  EVENT_MANAGER_PROVIDER,
  LANGLOCALE,
  MYRE_I18N_SCOPE,
  PROTOCOL_PROVIDER,
  TRANSLATOR,
  WINDOW_PROVIDER,
  type Translator,
} from '@myre/ui-primitive-core';
import {
  FORMS_CONFIG,
  GOOGLE_MAP_API_KEY,
  type FormsConfig,
} from '@myre/ui-primitive-forms';

import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ErrorHandler,
  LOCALE_ID,
  makeEnvironmentProviders,
  Type,
  type EnvironmentProviders,
  type Provider,
} from '@angular/core';
import { DefaultUrlSerializer, UrlSerializer, UrlTree } from '@angular/router';
import {
  provideTranslateService,
  TranslateLoader,
  TranslateParser,
  TranslateService,
  TranslateStore,
} from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { Observable } from 'rxjs';

import { COOKIE_PREFIX, cookieOptions } from './cookies/cookies.constants';
import { angularLocales } from './helpers/locales.helpers';
import { ApiErrorInterceptor } from './interceptors/api-error.interceptor';
import {
  CredentialsInterceptor,
  MfaInterceptor,
} from './interceptors/credentials.interceptor';
import { MyreTranslateLoader, WORDINGS } from './myre-translate-loader';
import { MyreTranslateParser } from './myre-translate-parser';
import { MyreTranslateStore } from './myre-translate-store';
import {
  BaseAuthService,
  MYRE_APP,
  type BaseContextIds,
} from './services/auth/auth.service';
import {
  CONTEXT_IDS,
  RouterStateSerializer,
  STRINGS_PARAM_KEYS,
} from './services/auth/dispatch.guard';
import { CustomErrorHandler } from './services/errors/error-handler.service';
import {
  provideZendesk,
  ZendeskConfig,
} from './services/zendesk/zendesk.module';

for (const angularLocale of Object.values(angularLocales)) {
  registerLocaleData(angularLocale);
}

function formsProviderFactory(): FormsConfig {
  return {
    minDate: new Date(1900, 0, 1),
    maxDate: new Date(2100, 0, 1),
    minNumber: Number.MIN_SAFE_INTEGER,
    maxNumber: Number.MAX_SAFE_INTEGER,
  };
}

export function translatorProviderFactory(
  translateService: TranslateService,
): Translator {
  return (key, textBindings) =>
    key ? translateService.instant(key, textBindings) : key;
}

export function langLocaleProviderFactory(
  cookieService: CookieService,
  dynamicEnvService: DynamicEnvService,
): string {
  let lang = cookieService.get(
    COOKIE_PREFIX + dynamicEnvService.env.cookieSuffix,
  ) as LangLocale;

  if (!lang || !languages[lang]) {
    lang = defaultLangLocale;

    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);

    const options = Object.assign({}, cookieOptions);
    options.expires = expires;
    options.domain = dynamicEnvService.env.domain;
    options.secure = false;
    options.path = '/';

    cookieService.set(
      COOKIE_PREFIX + dynamicEnvService.env.cookieSuffix,
      lang,
      options,
    );
  }

  return lang;
}

export function localeIdProviderFactory(lang: LangLocale): string {
  return languages[lang].angularLocale;
}

export interface MyreModuleConfig {
  app?: MyreApp;
  authService?: Type<BaseAuthService>;
  i18nScope?: Scope | Scope[];
  routerStateSerializer?: {
    class: Type<RouterStateSerializer>;
    contextIds: BaseContextIds;
    stringsParamKeys?: string[];
  };
}

export class CustomUrlSerializer extends DefaultUrlSerializer {
  /**
   * Removes parentheses from primary child routes of property
   */
  public override serialize(tree: UrlTree): string {
    return super.serialize(tree).replace(/\(([\w-/]+)\)/g, '$1');
  }
}

function googleMapApiKeyProviderFactory(
  dynamicEnvService: DynamicEnvService,
): string {
  return dynamicEnvService.env.googleMap?.key;
}

export function initFactory(
  translateService: TranslateService,
  lang: LangLocale,
): () => Observable<unknown> {
  return () => translateService.use(lang);
}

export function provideMyre(config?: MyreModuleConfig): EnvironmentProviders {
  const providers: Array<Provider | EnvironmentProviders> = [
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useClass: MyreTranslateLoader,
      },
      parser: {
        provide: TranslateParser,
        useClass: MyreTranslateParser,
      },
    }),
    provideZendesk(ZendeskConfig),
    { provide: TranslateStore, useClass: MyreTranslateStore },
    {
      provide: APP_INITIALIZER,
      useFactory: initFactory,
      deps: [TranslateService, LANGLOCALE],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useExisting: CredentialsInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useExisting: MfaInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useExisting: ApiErrorInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: CustomErrorHandler,
    },
    {
      provide: GOOGLE_MAP_API_KEY,
      useFactory: googleMapApiKeyProviderFactory,
      deps: [DynamicEnvService],
    },
    EVENT_MANAGER_PROVIDER,
    WINDOW_PROVIDER,
    {
      provide: TRANSLATOR,
      useFactory: translatorProviderFactory,
      deps: [TranslateService],
    },
    {
      provide: LANGLOCALE,
      useFactory: langLocaleProviderFactory,
      deps: [CookieService, DynamicEnvService],
    },
    {
      provide: LOCALE_ID,
      useFactory: localeIdProviderFactory,
      deps: [LANGLOCALE],
    },
    APP_BASE_HREF_PROVIDER,
    {
      provide: FORMS_CONFIG,
      useFactory: formsProviderFactory,
    },
    {
      provide: WORDINGS,
      useFactory: (translateLoader: MyreTranslateLoader) =>
        translateLoader.wordings,
      deps: [TranslateLoader],
    },
    PROTOCOL_PROVIDER,
    { provide: UrlSerializer, useClass: CustomUrlSerializer },
  ];

  if (config?.app) {
    providers.push({
      provide: MYRE_APP,
      useValue: config.app,
    });
  }

  if (config?.i18nScope) {
    providers.push({
      provide: MYRE_I18N_SCOPE,
      useValue: config.i18nScope,
    });
  }

  if (config?.authService) {
    providers.push({
      provide: BaseAuthService,
      useExisting: config.authService,
    });
  }

  if (config?.routerStateSerializer) {
    if (config.routerStateSerializer.stringsParamKeys) {
      providers.push({
        provide: STRINGS_PARAM_KEYS,
        useValue: config.routerStateSerializer.stringsParamKeys,
      });
    }

    providers.push(
      {
        provide: CONTEXT_IDS,
        useValue: config.routerStateSerializer.contextIds,
      },
      config.routerStateSerializer.class,
      {
        provide: RouterStateSerializer,
        useExisting: config.routerStateSerializer.class,
      },
    );
  }

  return makeEnvironmentProviders(providers);
}
